import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import AdminPortalLayout from './AdminPortalLayout';
import { AdminMe, AnalyticsBundle, fetchAnalytics } from '../lib/admin-portal-api';
import { FaApple, FaAndroid, FaUserPlus, FaStar, FaDollarSign, FaClipboardList, FaExclamationTriangle } from '../lib/icons';

interface Props { me: AdminMe }

export default function AdminPortalAnalytics({ me }: Props) {
  const [data, setData] = useState<AnalyticsBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (!me.isSuperAdmin) return <Navigate to="/admin-portal" replace />;

  return (
    <AdminPortalLayout me={me} active="analytics">
      <Helmet><title>Analytics — EatMeat Admin</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Analytics</h1>
        <p className="text-sm text-gray-500 mt-1">High-level platform snapshot. Real numbers, last 30 days where applicable.</p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 inline-flex items-start gap-2 mb-4">
          <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Top counters */}
          <Section title="Users">
            <Stat label="Total" value={data.users.total} IconC={FaUserPlus} tint="from-blue-50 to-blue-100/40" text="text-blue-700" />
            <Stat label="Premium" value={data.users.premium} IconC={FaStar} tint="from-purple-50 to-purple-100/40" text="text-purple-700" />
            <Stat label="New (30d)" value={data.users.new30} IconC={FaUserPlus} tint="from-green-50 to-green-100/40" text="text-green-700" sub={`${data.users.new7} in last 7d`} />
            <Stat label="Banned" value={data.users.banned} IconC={FaClipboardList} tint="from-red-50 to-red-100/40" text="text-red-700" />
          </Section>

          {/* Subscriptions */}
          <Section title="Subscriptions">
            <Stat label="Active" value={data.subscriptions.active} IconC={FaStar} tint="from-green-50 to-green-100/40" text="text-green-700" />
            <Stat label="Past due" value={data.subscriptions.past_due} IconC={FaStar} tint="from-amber-50 to-amber-100/40" text="text-amber-700" />
            <Stat label="Canceled" value={data.subscriptions.canceled} IconC={FaStar} tint="from-gray-50 to-gray-100/40" text="text-gray-700" />
            <Stat label="Expired" value={data.subscriptions.expired} IconC={FaStar} tint="from-red-50 to-red-100/40" text="text-red-700" />
          </Section>

          {/* Trends */}
          <div className="grid md:grid-cols-2 gap-3">
            <ChartCard title="Daily signups · 30d" data={data.trends.daily_signups} color="bg-blue-300" />
            <ChartCard title="Daily paid conversions · 30d" data={data.trends.daily_paid_conversions} color="bg-purple-300" />
          </div>

          {/* Devices + Community + Referrals row */}
          <div className="grid md:grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">Devices</h3>
              <DeviceBar IconC={FaApple} label="iOS" value={data.devices.ios} total={data.devices.ios + data.devices.android + data.devices.unknown} color="bg-gray-800" />
              <DeviceBar IconC={FaAndroid} label="Android" value={data.devices.android} total={data.devices.ios + data.devices.android + data.devices.unknown} color="bg-green-500" />
              <DeviceBar label="Unknown" value={data.devices.unknown} total={data.devices.ios + data.devices.android + data.devices.unknown} color="bg-gray-300" />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">Community</h3>
              <dl className="space-y-2 text-sm">
                <Row label="Live posts" value={data.community.live} />
                <Row label="Pending review" value={data.community.pending} highlight={data.community.pending > 0 ? 'text-amber-700' : undefined} />
                <Row label="Hidden" value={data.community.hidden} />
                <Row label="Total comments" value={data.community.total_comments} />
              </dl>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3 inline-flex items-center gap-2"><FaDollarSign className="text-orange-500" /> Referrals · 30d</h3>
              <dl className="space-y-2 text-sm">
                <Row label="Active influencers" value={`${data.referrals.active} / ${data.referrals.total}`} />
                <Row label="Gross revenue" value={`$${data.referrals.revenue_30d.toFixed(2)}`} />
                <Row label="Commission accrued" value={`$${data.referrals.commission_30d.toFixed(2)}`} highlight="text-green-700" />
              </dl>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPortalLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-bold text-gray-700 text-sm uppercase tracking-wider mb-2">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

function Stat({ label, value, sub, IconC, tint, text }: { label: string; value: number | string; sub?: string; IconC?: any; tint: string; text: string }) {
  const Icon = IconC;
  return (
    <div className={`relative overflow-hidden rounded-2xl px-4 py-4 bg-gradient-to-br ${tint} ring-1 ring-gray-100 shadow-sm`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
        {Icon && <Icon className={`text-lg opacity-60 ${text}`} />}
      </div>
      <div className={`text-3xl font-extrabold mt-2 ${text}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, data, color }: { title: string; data: Record<string, number>; color: string }) {
  const entries = Object.entries(data);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-800">{title}</h3>
        <span className="text-[11px] text-gray-400">{total} total</span>
      </div>
      <div className="flex items-end gap-0.5 h-24">
        {entries.map(([k, v]) => {
          const h = (v / max) * 100;
          return (
            <div key={k} title={`${k}: ${v}`}
              className={`flex-1 rounded-sm transition-all ${v > 0 ? color : 'bg-gray-100'}`}
              style={{ height: `${Math.max(h, 4)}%` }} />
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
        <span>30 days ago</span><span>Today</span>
      </div>
    </div>
  );
}

function DeviceBar({ label, value, total, color, IconC }: { label: string; value: number; total: number; color: string; IconC?: any }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const Icon = IconC;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between mb-1.5">
        <span className="text-sm font-medium inline-flex items-center gap-2">
          {Icon && <Icon className="text-gray-600" />}
          {label}
        </span>
        <span className="text-sm text-gray-500 font-semibold">{value} ({pct}%)</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div className={`${color} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string | number; highlight?: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`font-semibold ${highlight || 'text-gray-800'}`}>{value}</dd>
    </div>
  );
}
